import { USER_FEEDBACK } from '../APIErrors';
import {
  CartSchema,
  Product,
  Cart,
  AddressModelWithoutAnalytics,
  GetCartResponseModel,
  GetCartResponseModelSchema,
  UpdateProductQuantityResponseModel,
  UpdateProductQuantityResponseModelSchema,
  AddCouponResponseModelSchema,
  ReserveCartRequest,
  ReserveCartResponse,
  ReserveCartResponseSchema,
} from './cartAPISchema';
import { LogErrorResponse } from '../Logging/LoggingAPI';
import Quantity from 'Scripts/global/modules/quantity_wp';

const API_URL = '/api/cart';

/**
 * Adds a product to the shopping cart.
 *
 * @param code - The code/productId of the product to be added.
 * @param warehouseCode - Optional warehouse code to validate the add against
 * (e.g. a click & collect store's warehouse). When omitted, validated against
 * the default online warehouse.
 * @returns A Promise that resolves to the updated shopping cart.
 * @throws An error if the HTTP request fails or the response is not valid.
 */
export async function addProductToCart(code: string, warehouseCode?: string): Promise<UpdateProductQuantityResponseModel> {
  const url = `${API_URL}`;
  const formData = {
    Code: code,
    Quantity: 1,
    WarehouseCode: warehouseCode,
  };

  const body = JSON.stringify(formData);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: body,
  });

  if (!response.ok) {
    await LogErrorResponse(url, response, body);
    return Promise.reject(USER_FEEDBACK.GENERIC);
  }

  const data = await response.json();
  const validatedData = UpdateProductQuantityResponseModelSchema.parse(data);

  return validatedData;
}

/**
 * Retrieves the shopping cart from the API.
 *
 * @param warehouseCode - Optional warehouse code to check inventory against (e.g. a
 * click & collect store's warehouse). When omitted, inventory is checked against the
 * default online warehouse. Kept as a plain `string | undefined` rather than a named
 * type alias — it's threaded through only a handful of call sites
 * (`loadShoppingCart`, `refetchCartForOrderType`, `useOrderTypeToggle`) and this
 * doc comment is the single source of truth for the "undefined means online
 * default" convention; a type alias would add indirection without adding safety.
 * @returns A Promise that resolves to the shopping cart data.
 * @throws An error if the HTTP request fails or the response is not valid.
 */
export async function getCart(warehouseCode?: string): Promise<GetCartResponseModel> {
  const url = warehouseCode ? `${API_URL}/${warehouseCode}` : `${API_URL}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    },
  });

  if (!response.ok) {
    await LogErrorResponse(url, response);
    Promise.reject(await response.json());
  }

  const data = await response.json();
  const validatedData = GetCartResponseModelSchema.parse(data);
  return validatedData;
}

/**
 * Updates the quantity of a product in the shopping cart.
 *
 * @param code - The code of the product.
 * @param quantity - The new quantity of the product.
 * @param isAdditive - Whether `quantity` is a delta to apply, or the new total.
 * @param signal - An AbortSignal object that allows cancelling the request.
 * @param warehouseCode - Optional warehouse code to validate the new quantity
 * against (e.g. a click & collect store's warehouse). When omitted, validated
 * against the default online warehouse.
 * @returns A Promise that resolves to the updated shopping cart.
 * @throws If the request fails or is aborted.
 */
export async function updateProductQuantity(
  code: string,
  quantity: number,
  isAdditive: boolean,
  signal: AbortSignal,
  warehouseCode?: string
): Promise<UpdateProductQuantityResponseModel> {
  const url = `${API_URL}/${code}`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      quantity: quantity,
      isAdditive: isAdditive,
      warehouseCode: warehouseCode,
    }),
    signal: signal,
  });

  if (!response.ok) {
    await LogErrorResponse(url, response);
    return Promise.reject(await response.json());
  }

  const data = await response.json();
  const validatedData = UpdateProductQuantityResponseModelSchema.parse(data);

  return validatedData;
}

/**
 * Removes a product from the shopping cart.
 *
 * @param code - The code/productId of the product to be deleted.
 * @param warehouseCode - Optional warehouse code to re-check inventory
 * against after removal (e.g. a click & collect store's warehouse). When
 * omitted, checked against the default online warehouse.
 * @returns A Promise that resolves to the updated shopping cart.
 * @throws An error if the HTTP request fails or the response is not valid.
 */
export async function removeProductFromCart(
  code: string,
  warehouseCode?: string
): Promise<UpdateProductQuantityResponseModel> {
  const url = warehouseCode
    ? `${API_URL}/${code}?warehouseCode=${encodeURIComponent(warehouseCode)}`
    : `${API_URL}/${code}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    await LogErrorResponse(url, response);
    return Promise.reject(USER_FEEDBACK.GENERIC);
  }

  const data = await response.json();
  const validatedData = UpdateProductQuantityResponseModelSchema.parse(data);

  return validatedData;
}

/**
 * Adds a discount code to the shopping cart.
 *
 * @param discountCode The discount code to be added.
 * @returns A Promise that resolves to the updated shopping cart.
 */
export async function addDiscountCode(discountCode: string): Promise<Cart> {
  const url = `${API_URL}/coupon`;
  const body = JSON.stringify({
    couponCode: discountCode,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: body,
  });

  if (!response.ok) {
    return Promise.reject(USER_FEEDBACK.COULD_NOT_ADD_DISCOUNT_CODE);
  }

  const data = await response.json();
  const validatedData = AddCouponResponseModelSchema.parse(data);

  if (validatedData.couponAdded && validatedData.cart) {
    return validatedData.cart;
  }

  return Promise.reject(validatedData.couponNotAddedInfo);
}

/**
 * Removes a discount code from the shopping cart.
 *
 * @param discountCode - The discount code to be removed.
 * @returns A Promise that resolves to the updated shopping cart.
 * @throws An error if there is an HTTP error during the API call.
 */
export async function removeDiscountCode(discountCode: string): Promise<Cart> {
  const url = `${API_URL}/coupon`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      couponCode: discountCode,
    }),
  });

  if (!response.ok) {
    await LogErrorResponse(url, response);
    return Promise.reject(USER_FEEDBACK.GENERIC);
  }

  const data = await response.json();
  const validatedData = CartSchema.parse(data);
  return validatedData;
}

/**
 * Updates the customer's address information.
 *
 * @param addressModel - The address model to be updated. It can be a single AddressModel object or an array of AddressModel objects.
 * @returns A Promise that resolves to a boolean indicating whether the update was successful.
 */
export async function updateCustomer(
  addressModel: AddressModelWithoutAnalytics | Array<AddressModelWithoutAnalytics>
): Promise<boolean> {
  const url = `${API_URL}/address`;

  if (!Array.isArray(addressModel)) {
    addressModel = [addressModel];
  }

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(addressModel),
  });

  if (!response.ok) {
    await LogErrorResponse(url, response);
    return Promise.reject(false);
  }
  return Promise.resolve(true);
}

/**
 * Creates a click & collect reservation for the current cart.
 *
 * @param request - The selected warehouse and the customer's contact details.
 * @returns A Promise that resolves to the reservation result (`createdOk`, an
 * optional `errorMessage`, and the created order when successful).
 * @throws An error if the HTTP request fails or the response is not valid.
 */
export async function reserveCart(request: ReserveCartRequest): Promise<ReserveCartResponse> {
  const url = `${API_URL}/reserve`;
  const body = JSON.stringify(request);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: body,
  });

  if (!response.ok) {
    await LogErrorResponse(url, response, body);
    return Promise.reject(USER_FEEDBACK.GENERIC);
  }

  const data = await response.json();
  return ReserveCartResponseSchema.parse(data);
}
